import { useState, useEffect } from 'react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { SegmentProcessResult } from '../types'
import { PdfCards, PdfSections, SegmentReportPdf } from './SegmentReportPdf'
import { isSectionVisible } from '../config/dashboard-sections.config'
import { computeGroupTicketMedio, computeGroupRoi } from '../utils/group-metrics'
import { formatRoiInsight, formatRoiValue } from '../utils/roi-copy'
import { buildExclusionSummary } from '../utils/exclusion-summary'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function formatCompact(value: number): string {
  const abs = Math.abs(value || 0)
  if (abs >= 1_000_000_000)
    return `R$ ${(value / 1_000_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} bi`
  if (abs >= 1_000_000)
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi`
  return formatCurrency(value)
}

// Tooltip component for metric explanations
function Tooltip({ text }: { text: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: '#e5e7eb',
        color: '#6b7280',
        fontSize: 10,
        fontWeight: 600,
        cursor: 'help',
        marginLeft: 4,
        verticalAlign: 'middle',
        position: 'relative'
      }}
      title={text}
    >
      ?
    </span>
  )
}

// ROI quality badge
function RoiBadge({ roi }: { roi: number | null }) {
  if (roi === null) return null
  const config =
    roi >= 3
      ? { label: 'Excelente', bg: '#d1fae5', color: '#065f46' }
      : roi >= 1
        ? { label: 'Positivo', bg: '#dbeafe', color: '#1e40af' }
        : roi >= 0
          ? { label: 'Baixo', bg: '#fef3c7', color: '#92400e' }
          : { label: 'Negativo', bg: '#fee2e2', color: '#991b1b' }
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        background: config.bg,
        color: config.color,
        marginLeft: 8
      }}
    >
      {config.label}
    </span>
  )
}

// Collapsible section
function CollapsibleSection({
  title,
  defaultOpen = false,
  count,
  children
}: {
  title: string
  defaultOpen?: boolean
  count?: number
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginTop: 20 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          fontSize: 14,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          textAlign: 'left'
        }}
      >
        <span
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            fontSize: 12
          }}
        >
          &#9654;
        </span>
        {title}
        {count !== undefined && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              textTransform: 'none',
              letterSpacing: 0,
              color: '#9ca3af'
            }}
          >
            ({count})
          </span>
        )}
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  )
}

export interface SegmentStatsCardProps {
  result: SegmentProcessResult
  startDate: string
  endDate: string
  datesChanged: boolean
  showToast: (msg: string) => void
}

// Segment stats card
export function SegmentStatsCard({
  result,
  startDate,
  endDate,
  showToast
}: SegmentStatsCardProps) {
  const {
    totals,
    ticketMedio,
    roi,
    averages,
    averagesFiltered,
    companies,
    segment,
    companiesProcessed,
    companiesFailed
  } = result

  const safeAveragesFiltered = averagesFiltered ?? {
    avgOrders: 0, avgRevenue: 0, avgInfluencedOrders: 0,
    avgInfluencedRevenue: 0, avgConversionRate: 0, companiesCount: 0
  }

  // Calculate total period days for display
  const totalPeriodDays = (() => {
    if (!startDate || !endDate) return null
    const s = new Date(startDate + 'T00:00:00').getTime()
    const e = new Date(endDate + 'T00:00:00').getTime()
    return Math.round((e - s) / (24 * 60 * 60 * 1000))
  })()

  const hasFilteredDiff =
    safeAveragesFiltered.companiesCount > 0 &&
    safeAveragesFiltered.companiesCount < companiesProcessed

  const primaryAvg = hasFilteredDiff ? safeAveragesFiltered : averages
  const primaryAvgCount = hasFilteredDiff
    ? safeAveragesFiltered.companiesCount
    : companiesProcessed

  // Procedência do recorte, a partir da partição disjunta do backend.
  // `companiesInCalc` é o número que de fato sustenta ROI, receita e ticket —
  // por isso vem primeiro na linha, e não o total analisado.
  const exclusionSummary = buildExclusionSummary(result.exclusions)
  const companiesInCalc = safeAveragesFiltered.companiesCount
  const totalConsidered = result.companiesTotal ?? companiesProcessed

  // PDF filter modal state
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [pdfSelectedPlans, setPdfSelectedPlans] = useState<string[]>(() =>
    roi.byPlanType.map(p => p.planType)
  )
  const [pdfSections, setPdfSections] = useState<PdfSections>({
    insight: true,
    resultados: true,
    ticketMedio: true,
    roiPlano: true,
    perfilComprador: true,
    ranking: true
  })
  const defaultCards: PdfCards = {
    receitaTotal: true,
    receitaInfluenciada: true,
    roiMedio: true,
    tmComWidde: true,
    tmSemWidde: true,
    tmDiferenca: true,
    rankingTop: true,
    rankingBottom: true
  }
  const [pdfCards, setPdfCards] = useState<PdfCards>(defaultCards)
  const [pdfSelectedInsights, setPdfSelectedInsights] = useState<boolean[]>([])

  // Eligible companies for ranking/PDF (success + orders + cost, exclude auto-excluded)
  const autoExcludeSet = new Set(result.autoExcludeKeys ?? [])
  const eligibleCompanies = companies.filter(
    c => c.success && c.totalOrders > 0 && c.proportionalCost > 0 && !autoExcludeSet.has(c.companyKey)
  )

  // Recalculate data based on selected plan types for PDF
  const allPlanTypes = roi.byPlanType.map(p => p.planType)
  const isFiltered =
    pdfSelectedPlans.length > 0 && pdfSelectedPlans.length < allPlanTypes.length
  const filteredCompanies = isFiltered
    ? eligibleCompanies.filter(c => pdfSelectedPlans.includes(c.planType))
    : eligibleCompanies
  // ticketMedio e roi (avgRoi/avgRoiWinsorized) dependem de QUAIS empresas entram
  // na conta. Sem isso, o filtro de plano recalculava só totals e roi.byPlanType —
  // os cards "ROI médio" e "Com Widde/Sem Widde" do PDF continuavam mostrando o
  // número do segmento INTEIRO mesmo com plano filtrado (FDN-1939, achado #8).
  const pdfData = isFiltered
    ? {
        totals: {
          totalOrders: filteredCompanies.reduce((s, c) => s + c.totalOrders, 0),
          totalRevenue: filteredCompanies.reduce((s, c) => s + c.totalRevenue, 0),
          influencedOrders: filteredCompanies.reduce((s, c) => s + c.influencedOrders, 0),
          influencedRevenue: filteredCompanies.reduce((s, c) => s + c.influencedRevenue, 0),
          directOrders: filteredCompanies.reduce((s, c) => s + c.directOrders, 0),
        },
        ticketMedio: computeGroupTicketMedio(filteredCompanies),
        roi: {
          ...computeGroupRoi(filteredCompanies),
          byPlanType: roi.byPlanType.filter(p => pdfSelectedPlans.includes(p.planType)),
        },
        averages,
        averagesFiltered: safeAveragesFiltered,
        companiesProcessed: filteredCompanies.length,
      }
    : { totals, ticketMedio, roi, averages, averagesFiltered: safeAveragesFiltered, companiesProcessed }

  // Ranking: Top 5 and Bottom 5 by ROI (respects user exclusions)
  const companiesWithRoi = eligibleCompanies.filter(c => c.roi !== null)
  const sortedByRoi = [...companiesWithRoi].sort(
    (a, b) => (b.roi ?? 0) - (a.roi ?? 0)
  )
  const top5 = sortedByRoi.slice(0, 5)
  const bottom5 = sortedByRoi.length > 5 ? sortedByRoi.slice(-5).reverse() : []

  // PDF-specific ranking (filtered by selected plans)
  const pdfCompaniesWithRoi = filteredCompanies.filter(
    c => c.roi !== null && !c.lowInvoice
  )
  const pdfSortedByRoi = [...pdfCompaniesWithRoi].sort(
    (a, b) => (b.roi ?? 0) - (a.roi ?? 0)
  )
  const pdfTop5 = pdfSortedByRoi.slice(0, 5)
  const pdfBottom5 =
    pdfSortedByRoi.length > 5 ? pdfSortedByRoi.slice(-5).reverse() : []

  // Build hero insight narrative
  const insightParts: string[] = []
  if (totals.influencedOrders > 0) {
    const pct =
      totals.totalOrders > 0
        ? ((totals.influencedOrders / totals.totalOrders) * 100).toFixed(0)
        : '0'
    insightParts.push(
      `A Widde influenciou ${totals.influencedOrders.toLocaleString('pt-BR')} pedidos (${pct}% do total), gerando ${formatCurrency(totals.influencedRevenue)} em receita.`
    )
  }
  if (ticketMedio.diffPercent !== null && ticketMedio.diffPercent > 0) {
    insightParts.push(
      `Clientes que interagiram com a Widde gastaram ${ticketMedio.diffPercent.toFixed(0)}% a mais por pedido.`
    )
  }
  if (roi.avgRoi !== null && roi.avgRoi > 1) {
    insightParts.push(formatRoiInsight(roi.avgRoi))
  }
  if (
    result?.buyerProfile?.diffReturningPercent !== null &&
    result?.buyerProfile?.diffReturningPercent !== undefined &&
    result?.buyerProfile?.diffReturningPercent > 0
  ) {
    insightParts.push(
      `Com Widde, ${result?.buyerProfile?.diffReturningPercent?.toFixed(1)}pp mais compradores sao retornantes, indicando maior fidelizacao.`
    )
  }

  // PDF insight parts (recalculated based on filtered plans)
  const pdfInsightParts: string[] = []
  if (pdfData.totals.influencedOrders > 0) {
    const pct =
      pdfData.totals.totalOrders > 0
        ? (
            (pdfData.totals.influencedOrders / pdfData.totals.totalOrders) *
            100
          ).toFixed(0)
        : '0'
    pdfInsightParts.push(
      `A Widde influenciou ${pdfData.totals.influencedOrders.toLocaleString('pt-BR')} pedidos (${pct}% do total), gerando ${formatCurrency(pdfData.totals.influencedRevenue)} em receita.`
    )
  }
  if (
    pdfData.ticketMedio.diffPercent !== null &&
    pdfData.ticketMedio.diffPercent > 0
  ) {
    pdfInsightParts.push(
      `Clientes que interagiram com a Widde gastaram ${pdfData.ticketMedio.diffPercent.toFixed(0)}% a mais por pedido.`
    )
  }
  if (pdfData.roi.avgRoi !== null && pdfData.roi.avgRoi > 1) {
    pdfInsightParts.push(formatRoiInsight(pdfData.roi.avgRoi))
  }
  if (
    result?.buyerProfile?.diffReturningPercent !== null &&
    result?.buyerProfile?.diffReturningPercent !== undefined &&
    result?.buyerProfile?.diffReturningPercent > 0
  ) {
    pdfInsightParts.push(
      `Com Widde, ${result?.buyerProfile?.diffReturningPercent?.toFixed(1)}pp mais compradores sao retornantes, indicando maior fidelizacao.`
    )
  }

  // Sync pdfSelectedInsights when insight count changes (e.g. plan filter changes)
  useEffect(() => {
    setPdfSelectedInsights(prev => {
      if (prev.length !== pdfInsightParts.length)
        return pdfInsightParts.map(() => true)
      return prev
    })
  }, [pdfInsightParts.length])

  // Copy handler
  const handleCopy = () => {
    const s = new Date(startDate)
    const e = new Date(endDate)
    const days =
      Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1
    const fmt = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
    const text = [
      `Segmento: ${segment} | ${fmt(s)} a ${fmt(e)} (${days} dias)`,
      `Empresas: ${companiesProcessed}${companiesFailed > 0 ? ` (${companiesFailed} falharam)` : ''}`,
      insightParts.length > 0 ? `\n${insightParts.join(' ')}\n` : '',
      `--- RESULTADOS PRINCIPAIS ---`,
      `Receita Influenciada: ${formatCurrency(totals.influencedRevenue)} (${totals.influencedOrders} pedidos)`,
      `Receita Total: ${formatCurrency(totals.totalRevenue)} (${totals.totalOrders} pedidos)`,
      `ROI Medio: ${roi.avgRoi !== null ? `${formatRoiValue(roi.avgRoi)}x` : '—'} (${roi.companiesWithRoi} empresas)`,
      ``,
      `--- TICKET MEDIO ---`,
      `Com Widde: ${formatCurrency(ticketMedio.avgTicketAttributedOrders)}`,
      `Sem Widde: ${formatCurrency(ticketMedio.avgTicketOrganic)}`,
      `Diferenca: ${ticketMedio.diffPercent !== null ? `${ticketMedio.diffPercent >= 0 ? '+' : ''}${ticketMedio.diffPercent.toFixed(1)}%` : '—'}`,
      ``,
      `--- MEDIAS POR EMPRESA (${primaryAvgCount} empresas${hasFilteredDiff ? ' com pedidos' : ''}) ---`,
      `Pedidos: ${primaryAvg.avgOrders.toFixed(1)} | Receita: ${formatCurrency(primaryAvg.avgRevenue)}`,
      `Influenciados: ${primaryAvg.avgInfluencedOrders.toFixed(1)} | Rec. Influenciada: ${formatCurrency(primaryAvg.avgInfluencedRevenue)}`,
      `Taxa de Conversao: ${primaryAvg.avgConversionRate.toFixed(1)}%`,
      ...(roi.byPlanType.length > 0
        ? [
            ``,
            `--- ROI POR PLANO ---`,
            ...roi.byPlanType.map(
              p =>
                `${p.planType}: ${p.avgRoi !== null ? `${formatRoiValue(p.avgRoi)}x` : '—'} (${p.companiesCount} empresas)`
            )
          ]
        : []),
      ...(top5.length > 0
        ? [
            ``,
            `--- TOP ${top5.length} ROI ---`,
            ...top5.map(
              (c, i) =>
                `${i + 1}. ${c.companyTitle}: ${formatRoiValue(c.roi!)}x (${formatCurrency(c.influencedRevenue)})`
            )
          ]
        : []),
      ...(bottom5.length > 0
        ? [
            ``,
            `--- BOTTOM ${bottom5.length} ROI ---`,
            ...bottom5.map(
              (c, i) =>
                `${sortedByRoi.length - bottom5.length + i + 1}. ${c.companyTitle}: ${formatRoiValue(c.roi!)}x (${formatCurrency(c.influencedRevenue)})`
            )
          ]
        : [])
    ]
      .filter(Boolean)
      .join('\n')
    navigator.clipboard.writeText(text).then(() => showToast('Copiado!'))
  }

  return (
    <div className="card" style={{ position: 'relative' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        <h2 style={{ margin: 0 }}>
          <span
            className="segment-badge"
            style={{ fontSize: 12, verticalAlign: 'middle' }}
          >
            Segmento
          </span>{' '}
          {segment}
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => {
              setPdfSelectedPlans(roi.byPlanType.map(p => p.planType))
              setPdfSections({
                insight: true,
                resultados: true,
                ticketMedio: true,
                roiPlano: true,
                perfilComprador: true,
                ranking: true
              })
              setPdfCards({ ...defaultCards })
              setPdfSelectedInsights(pdfInsightParts.map(() => true))
              setShowPdfModal(true)
            }}
            style={{
              background: '#7c3aed',
              border: 'none',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Baixar PDF
          </button>
          <button
            onClick={handleCopy}
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Copiar resumo
          </button>
          <button
            onClick={() => {
              const headers = [
                'Empresa',
                'Company Key',
                'Property ID',
                'Plano',
                'Status Assinatura',
                'Total Pedidos',
                'Receita Total (R$)',
                'Pedidos Influenciados',
                'Receita Influenciada (R$)',
                'Pedidos Diretos',
                'Ticket Medio (R$)',
                'Ticket Medio Atribuido - Item (R$)',
                'Ticket Medio Atribuido - Pedido (R$)',
                'Ticket Medio Organico (R$)',
                'ROI',
                'Custo Mensal (R$)',
                'Dias GA',
                '1o Mes Assinatura Parcial',
                'Data Assinatura',
                'Status'
              ]
              const rows = companies
                .filter(c => c.success && c.totalOrders > 0)
                .map(c => {
                  const periodDays = totalPeriodDays ?? 30
                  const companyDays = c.gaActiveDays ?? periodDays
                  const isExcluded = autoExcludeSet.has(c.companyKey)
                  let status = 'Incluida'
                  if (c.proportionalCost === 0) status = 'Sem custo no periodo'
                  else if (isExcluded) status = 'Excluida pelo usuario'
                  return [
                    c.companyTitle,
                    c.companyKey,
                    c.propertyId,
                    c.planType,
                    c.subscriptionStatus || '',
                    c.totalOrders,
                    c.totalRevenue.toFixed(2),
                    c.influencedOrders,
                    c.influencedRevenue.toFixed(2),
                    c.directOrders,
                    c.avgTicket.toFixed(2),
                    c.avgTicketInfluenced.toFixed(2),
                    c.avgTicketAttributedOrders.toFixed(2),
                    c.avgTicketOrganic.toFixed(2),
                    c.roi !== null ? c.roi.toFixed(2) : '',
                    c.proportionalCost.toFixed(2),
                    companyDays,
                    c.isFirstMonthPartial ? 'Sim' : 'Nao',
                    c.subscriptionCreatedAt
                      ? new Date(c.subscriptionCreatedAt).toLocaleDateString(
                          'pt-BR'
                        )
                      : '',
                    status
                  ]
                })
              const csvContent = [
                headers.join(';'),
                ...rows.map(row => row.map(v => `"${v}"`).join(';'))
              ].join('\n')
              const BOM = '﻿'
              const blob = new Blob([BOM + csvContent], {
                type: 'text/csv;charset=utf-8;'
              })
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = `resultados-${segment.toLowerCase().replace(/\s+/g, '-')}-${startDate}-a-${endDate}.csv`
              link.click()
              URL.revokeObjectURL(url)
            }}
            style={{
              background: '#059669',
              border: 'none',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Linha de procedência do recorte.
          Antes eram três contadores independentes que se sobrepunham: uma única
          empresa fora do cálculo aparecia como "1 auto-excluida", depois dentro
          de "2 excluidas do calculo geral", e ainda como "1 sem pedidos no
          periodo" — rótulo falso, já que ela tinha pedidos. Agora a fonte é a
          partição do backend (`exclusions`), onde cada empresa tem exatamente um
          motivo, e a identidade total = usadas + excluídas fecha sempre. */}
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        <strong>{companiesInCalc}</strong> empresas no calculo
        {totalConsidered > companiesInCalc && <> de {totalConsidered} consideradas</>}
        {exclusionSummary.length > 0 && (
          <>
            {' '}
            &middot;{' '}
            {exclusionSummary.map((group, i) => (
              <span key={group.reason} style={{ color: group.color }}>
                {i > 0 && ', '}
                {group.count} {group.label}
                {group.detail && `: ${group.detail}`}
              </span>
            ))}
          </>
        )}
      </div>

      {/* WARNING BANNER — partial GA data */}
      {companies.some(c => c.success && c.gaActiveDays !== undefined) && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span style={{ fontSize: 16 }}>&#9888;</span>
          <span>
            <strong>{companies.filter(c => c.success && c.gaActiveDays !== undefined).length}</strong> empresa(s) com
            dados parciais do GA (configuracao recente). Os dados podem estar
            abaixo do real.
          </span>
        </div>
      )}
      {companies.some(c => c.success && c.isFirstMonthPartial) && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#991b1b',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span style={{ fontSize: 16 }}>&#9888;</span>
          <span>
            <strong>{companies.filter(c => c.success && c.isFirstMonthPartial).length}</strong>{' '}
            empresa(s) com primeiro mes de assinatura incompleto no periodo. O
            custo mensal cheio esta sendo considerado, mas os dados sao parciais
            — o ROI pode estar subestimado.
          </span>
        </div>
      )}

      {/* COMPANY LIST — read-only, exclusions managed by backend health checks */}
      {isSectionVisible('empresas_no_calculo') && (
        <CollapsibleSection
          title={`Empresas no Calculo (${eligibleCompanies.length})`}
          count={eligibleCompanies.length}
        >
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            Empresas incluidas no calculo. Exclusoes sao gerenciadas automaticamente pelos health checks.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 600,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 2
              }}
            >
              <span style={{ flex: 1 }}>Empresa</span>
              <span style={{ minWidth: 80, textAlign: 'right' }}>Plano</span>
              <span style={{ minWidth: 70, textAlign: 'right' }}>Dias</span>
              <span style={{ minWidth: 60, textAlign: 'right' }}>ROI</span>
            </div>
            {[...eligibleCompanies]
              .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0))
              .map(c => {
                const periodDays = totalPeriodDays ?? 30
                const companyDays = c.gaActiveDays ?? periodDays
                const hasPartialData = c.gaActiveDays !== undefined
                return (
                  <div
                    key={c.companyKey}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      borderRadius: 6,
                      background: '#f9fafb',
                      fontSize: 13
                    }}
                  >
                    <span style={{ flex: 1, fontWeight: 500 }}>
                      {c.companyTitle}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: 11 }}>
                      {c.planType}
                    </span>
                    <span
                      style={{
                        color: hasPartialData ? '#d97706' : '#059669',
                        fontSize: 11,
                        fontWeight: 600,
                        minWidth: 70,
                        textAlign: 'right'
                      }}
                    >
                      {hasPartialData && '⚠ '}
                      {companyDays} dias
                    </span>
                    <span
                      style={{
                        minWidth: 60,
                        textAlign: 'right',
                        fontWeight: 600,
                        fontSize: 12,
                        color:
                          c.roi !== null
                            ? c.roi >= 0
                              ? '#059669'
                              : '#dc2626'
                            : '#9ca3af'
                      }}
                    >
                      {c.roi !== null ? `${formatRoiValue(c.roi)}x` : '—'}
                    </span>
                  </div>
                )
              })}
          </div>
        </CollapsibleSection>
      )}

      {/* HERO INSIGHT - narrative summary */}
      {insightParts.length > 0 && (
        <div
          style={{
            background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            borderLeft: '4px solid #7c3aed'
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#7c3aed',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 6
            }}
          >
            Resumo em uma frase
          </div>
          <div style={{ fontSize: 15, color: '#1f1f3a', lineHeight: 1.5 }}>
            {insightParts.join(' ')}
          </div>
        </div>
      )}

      {/* Data accuracy warning for pre-March 2026 periods */}
      {startDate && startDate < '2026-03-01' && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 16,
          fontSize: 13,
          color: '#92400e',
        }}>
          <strong>Dados com precisao limitada:</strong> Periodos anteriores a marco de 2026
          usam matching de produtos por nome via API, que pode ter inconsistencias se nomes
          de produtos foram alterados apos a compra. A partir de marco/2026, os dados sao
          mais precisos pois usam snapshots dos produtos no momento da interacao do usuario.
        </div>
      )}

      {/* PRIMARY METRICS - "A Widde funciona?" */}
      <h3
        style={{
          fontSize: 14,
          color: '#6b7280',
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      >
        Resultado Principal
      </h3>
      <div className="dashboard-stats">
        <div
          className="stat-card"
          style={{ cursor: 'pointer', borderLeft: '3px solid #7c3aed' }}
          onClick={() => {
            navigator.clipboard.writeText(
              formatCurrency(totals.influencedRevenue)
            )
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">
            Receita gerada pela Widde
            <Tooltip text="Valor total de vendas em que o cliente assistiu um video da Widde antes de comprar" />
          </div>
          <div
            className="stat-value"
            style={{ color: '#7c3aed', fontSize: 22 }}
          >
            {formatCurrency(totals.influencedRevenue)}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {totals.influencedOrders} pedidos de {totals.totalOrders} total
          </div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', borderLeft: '3px solid #059669' }}
          onClick={() => {
            const text =
              roi.avgRoi !== null ? `${formatRoiValue(roi.avgRoi)}x` : '—'
            navigator.clipboard.writeText(text)
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">
            Retorno do investimento (ROI)
            <Tooltip text="Para cada R$1 que a empresa paga de Widde, quantos reais ela gera em vendas influenciadas" />
          </div>
          <div
            className="stat-value"
            style={{
              fontSize: 22,
              color:
                roi.avgRoi !== null
                  ? roi.avgRoi >= 0
                    ? '#059669'
                    : '#dc2626'
                  : '#9ca3af'
            }}
          >
            {roi.avgRoi !== null ? `${formatRoiValue(roi.avgRoi)}x` : '—'}
            <RoiBadge roi={roi.avgRoi} />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {roi.companiesWithRoi} de {companiesProcessed} empresas
          </div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', borderLeft: '3px solid #6366f1' }}
          onClick={() => {
            navigator.clipboard.writeText(
              `${primaryAvg.avgConversionRate.toFixed(1)}%`
            )
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">
            Pedidos com Widde
            <Tooltip text="De todos os pedidos da loja, qual porcentagem veio de clientes que assistiram videos da Widde" />
          </div>
          <div
            className="stat-value"
            style={{ color: '#6366f1', fontSize: 22 }}
          >
            {primaryAvg.avgConversionRate.toFixed(1)}%
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            do total de pedidos
          </div>
        </div>
      </div>

      {/* TICKET MEDIO - "A Widde traz pedidos melhores?" */}
      {isSectionVisible('valor_dos_pedidos') && (
        <>
          <h3
            style={{
              fontSize: 14,
              color: '#6b7280',
              marginBottom: 10,
              marginTop: 24,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Valor dos Pedidos
            <Tooltip text="Compara quanto cada cliente gasta em media: os que viram videos da Widde vs os que nao viram" />
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12
            }}
          >
            <div
              className="stat-card"
              style={{
                cursor: 'pointer',
                background: '#f5f3ff',
                borderLeft: '3px solid #7c3aed'
              }}
              onClick={() => {
                navigator.clipboard.writeText(
                  formatCurrency(ticketMedio.avgTicketAttributedOrders)
                )
                showToast('Copiado!')
              }}
            >
              <div className="stat-label">Com Widde</div>
              <div className="stat-value" style={{ color: '#7c3aed' }}>
                {formatCurrency(ticketMedio.avgTicketAttributedOrders)}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                por pedido
              </div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(
                  formatCurrency(ticketMedio.avgTicketOrganic)
                )
                showToast('Copiado!')
              }}
            >
              <div className="stat-label">Sem Widde</div>
              <div className="stat-value">
                {formatCurrency(ticketMedio.avgTicketOrganic)}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                por pedido
              </div>
            </div>
            <div
              className="stat-card"
              style={{
                cursor: 'pointer',
                background:
                  ticketMedio.diffPercent !== null &&
                  ticketMedio.diffPercent >= 0
                    ? '#ecfdf5'
                    : '#fef2f2',
                borderLeft: `3px solid ${ticketMedio.diffPercent !== null && ticketMedio.diffPercent >= 0 ? '#059669' : '#dc2626'}`
              }}
              onClick={() => {
                const text =
                  ticketMedio.diffPercent !== null
                    ? `${ticketMedio.diffPercent >= 0 ? '+' : ''}${ticketMedio.diffPercent.toFixed(1)}%`
                    : '—'
                navigator.clipboard.writeText(text)
                showToast('Copiado!')
              }}
            >
              <div className="stat-label">Diferenca</div>
              <div
                className="stat-value"
                style={{
                  color:
                    ticketMedio.diffPercent !== null
                      ? ticketMedio.diffPercent >= 0
                        ? '#059669'
                        : '#dc2626'
                      : '#9ca3af'
                }}
              >
                {ticketMedio.diffPercent !== null
                  ? `${ticketMedio.diffPercent >= 0 ? '+' : ''}${ticketMedio.diffPercent.toFixed(1)}%`
                  : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Widde vs sem Widde
              </div>
            </div>
          </div>
        </>
      )}

      {/* ROI by Plan Type */}
      {isSectionVisible('roi_por_plano') &&
        roi.byPlanType.length > 1 && (
          <>
            <h3
              style={{
                fontSize: 14,
                color: '#6b7280',
                marginBottom: 10,
                marginTop: 24,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              ROI por Plano
              <Tooltip text="Retorno do investimento separado por tipo de plano da Widde" />
            </h3>
            <div className="dashboard-stats">
              {roi.byPlanType.map(p => (
                <div
                  key={p.planType}
                  className="stat-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const text =
                      p.avgRoi !== null ? `${formatRoiValue(p.avgRoi)}x` : '—'
                    navigator.clipboard.writeText(text)
                    showToast('Copiado!')
                  }}
                >
                  <div className="stat-label">{p.planType}</div>
                  <div
                    className="stat-value"
                    style={{
                      color:
                        p.avgRoi !== null
                          ? p.avgRoi >= 0
                            ? '#059669'
                            : '#dc2626'
                          : '#9ca3af'
                    }}
                  >
                    {p.avgRoi !== null ? `${formatRoiValue(p.avgRoi)}x` : '—'}
                    <RoiBadge roi={p.avgRoi} />
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {p.companiesCount} empresas | Custo:{' '}
                    {formatCurrency(p.totalCost)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      {/* PERFIL DO COMPRADOR */}
      {isSectionVisible('perfil_do_comprador') &&
        result?.buyerProfile &&
        result?.buyerProfile.withVideo.newUsers +
          result?.buyerProfile.withVideo.returningUsers >
          0 &&
        (() => {
          const bp = result?.buyerProfile
          const totalWith = bp.withVideo.newUsers + bp.withVideo.returningUsers
          const totalWithout =
            bp.withoutVideo.newUsers + bp.withoutVideo.returningUsers
          const diff = bp.diffReturningPercent
          return (
            <>
              <h3
                style={{
                  fontSize: 14,
                  color: '#6b7280',
                  marginBottom: 10,
                  marginTop: 24,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Perfil do Comprador
                <Tooltip text="Compara a composicao de compradores novos vs retornantes entre sessoes com e sem interacao Widde (GA4 newVsReturning)." />
              </h3>
              <div className="dashboard-stats">
                <div
                  className="stat-card"
                  style={{
                    cursor: 'pointer',
                    background: '#f0fdf4',
                    borderLeft: '3px solid #059669'
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      bp.withVideo.returningPercent !== null
                        ? `${bp.withVideo.returningPercent.toFixed(1)}%`
                        : '—'
                    )
                    showToast('Copiado!')
                  }}
                >
                  <div className="stat-label">Com Widde</div>
                  <div className="stat-value" style={{ color: '#059669' }}>
                    {bp.withVideo.returningPercent !== null
                      ? `${bp.withVideo.returningPercent.toFixed(1)}%`
                      : '—'}{' '}
                    retornantes
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {totalWith} compras | {bp.withVideo.newUsers} novos,{' '}
                    {bp.withVideo.returningUsers} retornantes
                  </div>
                </div>
                <div
                  className="stat-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      bp.withoutVideo.returningPercent !== null
                        ? `${bp.withoutVideo.returningPercent.toFixed(1)}%`
                        : '—'
                    )
                    showToast('Copiado!')
                  }}
                >
                  <div className="stat-label">Sem Widde</div>
                  <div className="stat-value">
                    {bp.withoutVideo.returningPercent !== null
                      ? `${bp.withoutVideo.returningPercent.toFixed(1)}%`
                      : '—'}{' '}
                    retornantes
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {totalWithout} compras | {bp.withoutVideo.newUsers} novos,{' '}
                    {bp.withoutVideo.returningUsers} retornantes
                  </div>
                </div>
                <div
                  className="stat-card"
                  style={{
                    cursor: 'pointer',
                    background:
                      diff !== null && diff > 0
                        ? '#ecfdf5'
                        : diff !== null
                          ? '#fef2f2'
                          : '#fff',
                    borderLeft: `3px solid ${diff !== null && diff > 0 ? '#059669' : diff !== null ? '#dc2626' : '#9ca3af'}`
                  }}
                  onClick={() => {
                    const text =
                      diff !== null
                        ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pp`
                        : '—'
                    navigator.clipboard.writeText(text)
                    showToast('Copiado!')
                  }}
                >
                  <div className="stat-label">Diferenca</div>
                  <div
                    className="stat-value"
                    style={{
                      color:
                        diff !== null
                          ? diff > 0
                            ? '#059669'
                            : '#dc2626'
                          : '#9ca3af'
                    }}
                  >
                    {diff !== null
                      ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pp`
                      : '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {diff !== null && diff > 0
                      ? 'Widde fideliza mais'
                      : diff !== null
                        ? 'menos retornantes'
                        : 'dados insuficientes'}
                  </div>
                </div>
              </div>
            </>
          )
        })()}

      {/* RANKING - "Quem se destaca?" */}
      {isSectionVisible('ranking_roi') &&
        companiesWithRoi.length > 0 && (
          <>
            <h3
              style={{
                fontSize: 14,
                color: '#6b7280',
                marginBottom: 10,
                marginTop: 24,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Ranking por ROI
              <Tooltip text="Empresas com melhor e pior retorno do investimento no periodo" />
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: bottom5.length > 0 ? '1fr 1fr' : '1fr',
                gap: 16
              }}
            >
              <div
                style={{ background: '#f0fdf4', borderRadius: 8, padding: 12 }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#059669',
                    marginBottom: 8
                  }}
                >
                  Melhores resultados
                </div>
                {top5.map((c, i) => (
                  <div
                    key={c.companyKey}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background:
                        i % 2 === 0 ? 'rgba(255,255,255,0.6)' : 'transparent',
                      fontSize: 13
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${c.companyTitle}: ROI ${formatRoiValue(c.roi!)}x | ${formatCurrency(c.influencedRevenue)}`
                      )
                      showToast('Copiado!')
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          color: '#059669',
                          minWidth: 20
                        }}
                      >
                        {i + 1}.
                      </span>
                      <span style={{ color: '#111827' }}>{c.companyTitle}</span>
                    </div>
                    <div
                      style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                    >
                      <span style={{ fontWeight: 600, color: '#059669' }}>
                        {formatRoiValue(c.roi!)}x
                      </span>
                      <span style={{ color: '#6b7280', fontSize: 12 }}>
                        {formatCurrency(c.influencedRevenue)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {bottom5.length > 0 && (
                <div
                  style={{
                    background: '#fef2f2',
                    borderRadius: 8,
                    padding: 12
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#dc2626',
                      marginBottom: 8
                    }}
                  >
                    Oportunidades de melhoria
                  </div>
                  {bottom5.map((c, i) => (
                    <div
                      key={c.companyKey}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 8px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background:
                          i % 2 === 0 ? 'rgba(255,255,255,0.6)' : 'transparent',
                        fontSize: 13
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${c.companyTitle}: ROI ${formatRoiValue(c.roi!)}x | ${formatCurrency(c.influencedRevenue)}`
                        )
                        showToast('Copiado!')
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            color: '#dc2626',
                            minWidth: 20
                          }}
                        >
                          {sortedByRoi.length - bottom5.length + i + 1}.
                        </span>
                        <span style={{ color: '#111827' }}>
                          {c.companyTitle}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'center'
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: c.roi! >= 0 ? '#059669' : '#dc2626'
                          }}
                        >
                          {formatRoiValue(c.roi!)}x
                        </span>
                        <span style={{ color: '#6b7280', fontSize: 12 }}>
                          {formatCurrency(c.influencedRevenue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

      {/* COLLAPSIBLE: Medias por empresa */}
      <CollapsibleSection title="Medias por Empresa" count={primaryAvgCount}>
        {hasFilteredDiff && (
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            Considerando apenas empresas com pedidos no periodo (
            {safeAveragesFiltered.companiesCount} de {companiesProcessed})
          </div>
        )}
        <div className="dashboard-stats">
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(primaryAvg.avgOrders.toFixed(1))
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">
              Pedidos por empresa
              <Tooltip text="Numero medio de pedidos que cada empresa recebeu no periodo" />
            </div>
            <div className="stat-value">{primaryAvg.avgOrders.toFixed(1)}</div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(
                formatCurrency(primaryAvg.avgRevenue)
              )
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">
              Receita por empresa
              <Tooltip text="Valor medio de receita total que cada empresa faturou no periodo" />
            </div>
            <div className="stat-value">
              {formatCurrency(primaryAvg.avgRevenue)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(
                primaryAvg.avgInfluencedOrders.toFixed(1)
              )
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">
              Influenciados por empresa
              <Tooltip text="Numero medio de pedidos influenciados pela Widde em cada empresa" />
            </div>
            <div className="stat-value purple">
              {primaryAvg.avgInfluencedOrders.toFixed(1)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(
                formatCurrency(primaryAvg.avgInfluencedRevenue)
              )
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">
              Receita influenciada por empresa
              <Tooltip text="Valor medio de receita influenciada pela Widde em cada empresa" />
            </div>
            <div className="stat-value green">
              {formatCurrency(primaryAvg.avgInfluencedRevenue)}
            </div>
          </div>
        </div>

        {/* Show "todas" averages as secondary if there's a difference */}
        {hasFilteredDiff && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              background: '#f9fafb',
              borderRadius: 6,
              fontSize: 12,
              color: '#6b7280'
            }}
          >
            <strong>Incluindo todas as {companiesProcessed} empresas:</strong>{' '}
            {averages.avgOrders.toFixed(1)} pedidos |{' '}
            {formatCurrency(averages.avgRevenue)} receita |{' '}
            {averages.avgInfluencedOrders.toFixed(1)} influenciados |{' '}
            {averages.avgConversionRate.toFixed(1)}% conversao
          </div>
        )}
      </CollapsibleSection>

      {/* COLLAPSIBLE: Totais */}
      <CollapsibleSection title="Totais do Segmento">
        <div className="dashboard-stats">
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(String(totals.totalOrders))
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">Total de Pedidos</div>
            <div className="stat-value">{totals.totalOrders}</div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(formatCurrency(totals.totalRevenue))
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">Receita Total</div>
            <div className="stat-value">
              {formatCurrency(totals.totalRevenue)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(String(totals.influencedOrders))
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">Pedidos Influenciados</div>
            <div className="stat-value purple">{totals.influencedOrders}</div>
          </div>
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigator.clipboard.writeText(
                formatCurrency(totals.influencedRevenue)
              )
              showToast('Copiado!')
            }}
          >
            <div className="stat-label">Receita Influenciada</div>
            <div className="stat-value green">
              {totals.influencedRevenue > 0
                ? formatCurrency(totals.influencedRevenue)
                : '—'}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* COLLAPSIBLE: Per-company breakdown */}
      <CollapsibleSection
        title="Detalhamento por Empresa"
        count={companies.filter(c => !c.lowInvoice && c.totalOrders > 0).length}
      >
        <div className="segment-stats-grid">
          {companies
            .filter(c => !c.lowInvoice && c.totalOrders > 0)
            .map(c => (
              <div
                key={c.companyKey}
                className={`segment-company-card ${!c.success ? 'failed' : ''}`}
              >
                <div className="company-title">
                  {c.companyTitle}

                  {c.success && c.planType && (
                    <span
                      style={{
                        fontSize: 10,
                        color: '#6b7280',
                        marginLeft: 6,
                        fontWeight: 400
                      }}
                    >
                      {c.planType}
                    </span>
                  )}
                </div>
                {c.success ? (
                  <>
                    <div className="company-metric">
                      Pedidos: <strong>{c.totalOrders}</strong>
                    </div>
                    <div className="company-metric">
                      Receita: <strong>{formatCurrency(c.totalRevenue)}</strong>
                    </div>
                    <div className="company-metric">
                      Influenciados:{' '}
                      <strong style={{ color: '#7c3aed' }}>
                        {c.influencedOrders}
                      </strong>
                    </div>
                    <div className="company-metric">
                      Receita Inf.:{' '}
                      <strong style={{ color: '#059669' }}>
                        {formatCurrency(c.influencedRevenue)}
                      </strong>
                    </div>
                    <div className="company-metric">
                      ROI:{' '}
                      <strong
                        style={{
                          color:
                            c.roi !== null
                              ? c.roi >= 0
                                ? '#059669'
                                : '#dc2626'
                              : '#9ca3af'
                        }}
                      >
                        {c.roi !== null ? `${formatRoiValue(c.roi)}x` : '—'}
                      </strong>
                      {c.roi !== null && <RoiBadge roi={c.roi} />}
                    </div>
                    {c.effectiveStartDate && (
                      <div
                        className="company-metric"
                        style={{ color: '#d97706', fontSize: 11 }}
                      >
                        GA desde {c.effectiveStartDate}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="company-metric" style={{ color: '#dc2626' }}>
                    Erro: {c.error}
                  </div>
                )}
              </div>
            ))}
        </div>
      </CollapsibleSection>

      {/* PDF Preview Modal */}
      {showPdfModal &&
        (() => {
          const fmtD = (d: string) => {
            const [y, m, day] = d.split('-')
            return `${day}/${m}/${y}`
          }
          const days =
            Math.round(
              (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                (1000 * 60 * 60 * 24)
            ) + 1
          const toggleSection = (key: keyof PdfSections) =>
            setPdfSections(prev => ({ ...prev, [key]: !prev[key] }))
          const togglePlan = (planType: string) =>
            setPdfSelectedPlans(prev =>
              prev.includes(planType)
                ? prev.filter(t => t !== planType)
                : [...prev, planType]
            )
          const toggleCard = (key: keyof PdfCards) =>
            setPdfCards(prev => ({ ...prev, [key]: !prev[key] }))
          const toggleInsight = (idx: number) =>
            setPdfSelectedInsights(prev =>
              prev.map((v, i) => (i === idx ? !v : v))
            )
          const sectionStyle = (active: boolean): React.CSSProperties => ({
            position: 'relative',
            cursor: 'pointer',
            borderRadius: 6,
            transition: 'all 0.2s ease',
            opacity: active ? 1 : 0.35,
            filter: active ? 'none' : 'grayscale(100%)',
            border: active ? '2px solid transparent' : '2px dashed #d1d5db'
          })
          const toggleBadge = (active: boolean): React.CSSProperties => ({
            position: 'absolute',
            top: -8,
            right: -8,
            zIndex: 2,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: active ? '#7c3aed' : '#d1d5db',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            transition: 'all 0.2s ease'
          })
          const cardWrap = (
            active: boolean,
            sectionActive: boolean
          ): React.CSSProperties => ({
            flex: 1,
            cursor: sectionActive ? 'pointer' : 'default',
            opacity: sectionActive ? (active ? 1 : 0.3) : 1,
            border: active ? undefined : '1.5px dashed #d1d5db',
            borderRadius: 4,
            transition: 'all 0.15s',
            position: 'relative'
          })

          return (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
              }}
              onClick={() => setShowPdfModal(false)}
            >
              <div
                style={{
                  background: '#e5e7eb',
                  borderRadius: 16,
                  padding: 32,
                  boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
                  maxHeight: '92vh',
                  overflowY: 'auto',
                  width: 560
                }}
                onClick={e => e.stopPropagation()}
              >
                {/* Modal header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16
                  }}
                >
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, color: '#1f1f3a' }}>
                      Personalizar Relatorio
                    </h3>
                    <p
                      style={{
                        margin: '4px 0 0',
                        fontSize: 12,
                        color: '#6b7280'
                      }}
                    >
                      Clique nas secoes ou em cada card individual para
                      personalizar
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPdfModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 20,
                      color: '#9ca3af',
                      padding: 4
                    }}
                  >
                    &times;
                  </button>
                </div>

                {/* PDF Preview - simulated A4 page */}
                <div
                  style={{
                    background: '#fafafa',
                    borderRadius: 8,
                    padding: 20,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #d1d5db',
                    fontFamily: 'Helvetica, Arial, sans-serif'
                  }}
                >
                  {/* Hero (always visible) */}
                  <div
                    style={{
                      background: '#1a1a2e',
                      borderRadius: 6,
                      padding: '14px 16px',
                      marginBottom: 12
                    }}
                  >
                    <div
                      style={{
                        fontSize: 8,
                        color: '#7c3aed',
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        fontWeight: 700,
                        marginBottom: 4
                      }}
                    >
                      Relatorio de Performance
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: '#fff',
                        marginBottom: 2
                      }}
                    >
                      {segment}
                    </div>
                    <div style={{ fontSize: 9, color: '#a0a0b8' }}>
                      {fmtD(startDate)} a {fmtD(endDate)} | {days} dias |{' '}
                      {pdfData.companiesProcessed} empresas
                    </div>
                  </div>

                  {/* Insight section */}
                  {pdfInsightParts.length > 0 && (
                    <div
                      onClick={() => toggleSection('insight')}
                      style={sectionStyle(pdfSections.insight)}
                    >
                      <div style={toggleBadge(pdfSections.insight)}>
                        {pdfSections.insight ? '✓' : '✕'}
                      </div>
                      <div
                        style={{
                          background: '#ede9fe',
                          borderRadius: 4,
                          padding: '8px 10px',
                          marginBottom: 10,
                          borderLeft: '3px solid #7c3aed'
                        }}
                      >
                        <div
                          style={{
                            fontSize: 7,
                            color: '#7c3aed',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            fontWeight: 700,
                            marginBottom: 6
                          }}
                        >
                          Destaque
                        </div>
                        {pdfInsightParts.map((text, idx) => {
                          const active = pdfSelectedInsights[idx] !== false
                          return (
                            <div
                              key={idx}
                              onClick={e => {
                                e.stopPropagation()
                                if (pdfSections.insight) toggleInsight(idx)
                              }}
                              style={{
                                padding: '5px 8px',
                                borderRadius: 4,
                                marginBottom:
                                  idx < pdfInsightParts.length - 1 ? 4 : 0,
                                fontSize: 9,
                                color: '#2d2d3f',
                                lineHeight: 1.5,
                                cursor: pdfSections.insight
                                  ? 'pointer'
                                  : 'default',
                                opacity: active ? 1 : 0.3,
                                border: active
                                  ? '1px solid transparent'
                                  : '1px dashed #c4b5fd',
                                background: active
                                  ? 'rgba(255,255,255,0.4)'
                                  : 'rgba(245,243,255,0.6)',
                                transition: 'all 0.15s'
                              }}
                            >
                              {text}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Resultados Gerais section */}
                  <div
                    onClick={() => toggleSection('resultados')}
                    style={sectionStyle(pdfSections.resultados)}
                  >
                    <div style={toggleBadge(pdfSections.resultados)}>
                      {pdfSections.resultados ? '✓' : '✕'}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 700,
                          color: '#8b8ba3',
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          marginBottom: 6
                        }}
                      >
                        Resultados Gerais
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.resultados)
                              toggleCard('receitaTotal')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.receitaTotal,
                              pdfSections.resultados
                            ),
                            background: '#fff',
                            padding: '8px 6px',
                            textAlign: 'center',
                            border: pdfCards.receitaTotal
                              ? '1px solid #eeeef2'
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            Receita Total
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#2d2d3f'
                            }}
                          >
                            {formatCompact(pdfData.totals.totalRevenue)}
                          </div>
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              marginTop: 2
                            }}
                          >
                            {pdfData.totals.totalOrders.toLocaleString('pt-BR')}{' '}
                            pedidos
                          </div>
                        </div>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.resultados)
                              toggleCard('receitaInfluenciada')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.receitaInfluenciada,
                              pdfSections.resultados
                            ),
                            background: '#fff',
                            padding: '8px 6px',
                            textAlign: 'center',
                            border: pdfCards.receitaInfluenciada
                              ? '1.5px solid #7c3aed'
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#7c3aed',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            Receita Influenciada
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#7c3aed'
                            }}
                          >
                            {formatCompact(pdfData.totals.influencedRevenue)}
                          </div>
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              marginTop: 2
                            }}
                          >
                            {pdfData.totals.influencedOrders.toLocaleString(
                              'pt-BR'
                            )}{' '}
                            pedidos
                          </div>
                        </div>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.resultados) toggleCard('roiMedio')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.roiMedio,
                              pdfSections.resultados
                            ),
                            background:
                              pdfCards.roiMedio &&
                              pdfData.roi.avgRoi !== null &&
                              pdfData.roi.avgRoi >= 1
                                ? '#d1fae5'
                                : '#fff',
                            padding: '8px 6px',
                            textAlign: 'center',
                            border: pdfCards.roiMedio
                              ? `1.5px solid ${pdfData.roi.avgRoi !== null && pdfData.roi.avgRoi >= 1 ? '#059669' : '#eeeef2'}`
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            ROI Medio
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color:
                                pdfData.roi.avgRoi !== null
                                  ? pdfData.roi.avgRoi >= 0
                                    ? '#059669'
                                    : '#dc2626'
                                  : '#8b8ba3'
                            }}
                          >
                            {pdfData.roi.avgRoi !== null
                              ? `${formatRoiValue(pdfData.roi.avgRoi)}x`
                              : '—'}
                          </div>
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              marginTop: 2
                            }}
                          >
                            {pdfData.roi.companiesWithRoi} com ROI
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ticket Medio section */}
                  <div
                    onClick={() => toggleSection('ticketMedio')}
                    style={sectionStyle(pdfSections.ticketMedio)}
                  >
                    <div style={toggleBadge(pdfSections.ticketMedio)}>
                      {pdfSections.ticketMedio ? '✓' : '✕'}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 700,
                          color: '#8b8ba3',
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          marginBottom: 6
                        }}
                      >
                        Ticket Medio: Widde vs Organico
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.ticketMedio)
                              toggleCard('tmComWidde')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.tmComWidde,
                              pdfSections.ticketMedio
                            ),
                            background: pdfCards.tmComWidde
                              ? '#ede9fe'
                              : '#fff',
                            padding: '8px 6px',
                            textAlign: 'center',
                            border: pdfCards.tmComWidde
                              ? '1.5px solid #7c3aed'
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#7c3aed',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            Com Widde
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#7c3aed'
                            }}
                          >
                            {formatCurrency(
                              pdfData.ticketMedio.avgTicketAttributedOrders
                            )}
                          </div>
                        </div>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.ticketMedio)
                              toggleCard('tmSemWidde')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.tmSemWidde,
                              pdfSections.ticketMedio
                            ),
                            background: '#fff',
                            padding: '8px 6px',
                            textAlign: 'center',
                            border: pdfCards.tmSemWidde
                              ? '1px solid #eeeef2'
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            Sem Widde
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#2d2d3f'
                            }}
                          >
                            {formatCurrency(
                              pdfData.ticketMedio.avgTicketOrganic
                            )}
                          </div>
                        </div>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            if (pdfSections.ticketMedio)
                              toggleCard('tmDiferenca')
                          }}
                          style={{
                            ...cardWrap(
                              pdfCards.tmDiferenca,
                              pdfSections.ticketMedio
                            ),
                            padding: '8px 6px',
                            textAlign: 'center',
                            background: pdfCards.tmDiferenca
                              ? pdfData.ticketMedio.diffPercent !== null &&
                                pdfData.ticketMedio.diffPercent >= 0
                                ? '#d1fae5'
                                : '#fee2e2'
                              : '#fff',
                            border: pdfCards.tmDiferenca
                              ? `1.5px solid ${pdfData.ticketMedio.diffPercent !== null && pdfData.ticketMedio.diffPercent >= 0 ? '#059669' : '#dc2626'}`
                              : '1.5px dashed #d1d5db'
                          }}
                        >
                          <div
                            style={{
                              fontSize: 7,
                              color: '#8b8ba3',
                              textTransform: 'uppercase',
                              marginBottom: 3
                            }}
                          >
                            Diferenca
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color:
                                pdfData.ticketMedio.diffPercent !== null
                                  ? pdfData.ticketMedio.diffPercent >= 0
                                    ? '#059669'
                                    : '#dc2626'
                                  : '#8b8ba3'
                            }}
                          >
                            {pdfData.ticketMedio.diffPercent !== null
                              ? `${pdfData.ticketMedio.diffPercent >= 0 ? '+' : ''}${pdfData.ticketMedio.diffPercent.toFixed(1)}%`
                              : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ROI por Plano section */}
                  {roi.byPlanType.length > 0 && (
                    <div
                      onClick={() => toggleSection('roiPlano')}
                      style={sectionStyle(pdfSections.roiPlano)}
                    >
                      <div style={toggleBadge(pdfSections.roiPlano)}>
                        {pdfSections.roiPlano ? '✓' : '✕'}
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <div
                          style={{
                            fontSize: 8,
                            fontWeight: 700,
                            color: '#8b8ba3',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            marginBottom: 6
                          }}
                        >
                          ROI por Plano
                        </div>
                        <div
                          style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
                        >
                          {roi.byPlanType.map(p => {
                            const selected = pdfSelectedPlans.includes(
                              p.planType
                            )
                            return (
                              <div
                                key={p.planType}
                                onClick={e => {
                                  e.stopPropagation()
                                  if (pdfSections.roiPlano)
                                    togglePlan(p.planType)
                                }}
                                style={{
                                  flex: '1 1 0',
                                  minWidth: 70,
                                  background: '#fff',
                                  borderRadius: 4,
                                  padding: '6px 4px',
                                  textAlign: 'center',
                                  border: selected
                                    ? '1.5px solid #059669'
                                    : '1.5px dashed #d1d5db',
                                  opacity: pdfSections.roiPlano
                                    ? selected
                                      ? 1
                                      : 0.4
                                    : 1,
                                  cursor: pdfSections.roiPlano
                                    ? 'pointer'
                                    : 'default',
                                  transition: 'all 0.15s'
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: '#8b8ba3',
                                    textTransform: 'uppercase',
                                    marginBottom: 2
                                  }}
                                >
                                  {p.planType}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color:
                                      p.avgRoi !== null
                                        ? p.avgRoi >= 0
                                          ? '#059669'
                                          : '#dc2626'
                                        : '#8b8ba3'
                                  }}
                                >
                                  {p.avgRoi !== null
                                    ? `${formatRoiValue(p.avgRoi)}x`
                                    : '—'}
                                </div>
                                <div
                                  style={{
                                    fontSize: 6,
                                    color: '#8b8ba3',
                                    marginTop: 1
                                  }}
                                >
                                  {p.companiesCount} empresas
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Perfil do comprador section */}
                  {result?.buyerProfile &&
                    result?.buyerProfile.withVideo.newUsers +
                      result?.buyerProfile.withVideo.returningUsers >
                      0 && (
                      <div
                        onClick={() => toggleSection('perfilComprador')}
                        style={sectionStyle(pdfSections.perfilComprador)}
                      >
                        <div style={toggleBadge(pdfSections.perfilComprador)}>
                          {pdfSections.perfilComprador ? '✓' : '✕'}
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <div
                            style={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: '#8b8ba3',
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                              marginBottom: 6
                            }}
                          >
                            Perfil do Comprador
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <div
                              style={{
                                flex: 1,
                                background: '#d1fae5',
                                borderRadius: 4,
                                padding: 6,
                                border: '1px solid #059669',
                                textAlign: 'center' as const
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 6,
                                  color: '#6b7280',
                                  textTransform: 'uppercase' as const
                                }}
                              >
                                Com Widde
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: '#059669'
                                }}
                              >
                                {result?.buyerProfile.withVideo
                                  .returningPercent !== null
                                  ? `${result?.buyerProfile.withVideo.returningPercent.toFixed(0)}% ret.`
                                  : '—'}
                              </div>
                              <div style={{ fontSize: 5, color: '#6b7280' }}>
                                {result?.buyerProfile.withVideo.newUsers +
                                  result?.buyerProfile.withVideo
                                    .returningUsers}{' '}
                                compras
                              </div>
                            </div>
                            <div
                              style={{
                                flex: 1,
                                background: '#f5f5f5',
                                borderRadius: 4,
                                padding: 6,
                                border: '1px solid #d1d5db',
                                textAlign: 'center' as const
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 6,
                                  color: '#6b7280',
                                  textTransform: 'uppercase' as const
                                }}
                              >
                                Sem Widde
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: '#374151'
                                }}
                              >
                                {result?.buyerProfile.withoutVideo
                                  .returningPercent !== null
                                  ? `${result?.buyerProfile.withoutVideo.returningPercent.toFixed(0)}% ret.`
                                  : '—'}
                              </div>
                              <div style={{ fontSize: 5, color: '#6b7280' }}>
                                {result?.buyerProfile.withoutVideo.newUsers +
                                  result?.buyerProfile.withoutVideo
                                    .returningUsers}{' '}
                                compras
                              </div>
                            </div>
                            {result?.buyerProfile.diffReturningPercent !==
                              null && (
                              <div
                                style={{
                                  flex: 1,
                                  background:
                                    result?.buyerProfile.diffReturningPercent >
                                    0
                                      ? '#d1fae5'
                                      : '#fee2e2',
                                  borderRadius: 4,
                                  padding: 6,
                                  border: `1px solid ${result?.buyerProfile.diffReturningPercent > 0 ? '#059669' : '#dc2626'}`,
                                  textAlign: 'center' as const
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 6,
                                    color: '#6b7280',
                                    textTransform: 'uppercase' as const
                                  }}
                                >
                                  Diferenca
                                </div>
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color:
                                      result?.buyerProfile
                                        .diffReturningPercent > 0
                                        ? '#059669'
                                        : '#dc2626'
                                  }}
                                >
                                  {result?.buyerProfile.diffReturningPercent > 0
                                    ? '+'
                                    : ''}
                                  {result?.buyerProfile.diffReturningPercent.toFixed(
                                    0
                                  )}
                                  pp
                                </div>
                                <div style={{ fontSize: 5, color: '#6b7280' }}>
                                  {result?.buyerProfile.diffReturningPercent > 0
                                    ? 'Widde fideliza mais'
                                    : 'menos retornantes'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Ranking section */}
                  {pdfCompaniesWithRoi.length > 0 && (
                    <div
                      onClick={() => toggleSection('ranking')}
                      style={sectionStyle(pdfSections.ranking)}
                    >
                      <div style={toggleBadge(pdfSections.ranking)}>
                        {pdfSections.ranking ? '✓' : '✕'}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <div
                          style={{
                            fontSize: 8,
                            fontWeight: 700,
                            color: '#8b8ba3',
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            marginBottom: 6
                          }}
                        >
                          Ranking por ROI
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <div
                            onClick={e => {
                              e.stopPropagation()
                              if (pdfSections.ranking) toggleCard('rankingTop')
                            }}
                            style={{
                              ...cardWrap(
                                pdfCards.rankingTop,
                                pdfSections.ranking
                              ),
                              background: pdfCards.rankingTop
                                ? '#d1fae5'
                                : '#fff',
                              padding: 6,
                              border: pdfCards.rankingTop
                                ? '1px solid #059669'
                                : '1.5px dashed #d1d5db'
                            }}
                          >
                            <div
                              style={{
                                fontSize: 7,
                                fontWeight: 700,
                                color: '#059669',
                                textTransform: 'uppercase',
                                marginBottom: 4
                              }}
                            >
                              Top {Math.min(5, pdfTop5.length)}
                            </div>
                            {pdfTop5.slice(0, 3).map((co, i) => (
                              <div
                                key={co.companyKey}
                                style={{
                                  fontSize: 8,
                                  color: '#2d2d3f',
                                  marginBottom: 2,
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <span>
                                  {i + 1}.{' '}
                                  {co.companyTitle.length > 18
                                    ? co.companyTitle.slice(0, 18) + '...'
                                    : co.companyTitle}
                                </span>
                                <span
                                  style={{ fontWeight: 700, color: '#059669' }}
                                >
                                  {formatRoiValue(co.roi!)}x
                                </span>
                              </div>
                            ))}
                            {pdfTop5.length > 3 && (
                              <div
                                style={{
                                  fontSize: 7,
                                  color: '#8b8ba3',
                                  marginTop: 2
                                }}
                              >
                                +{pdfTop5.length - 3} mais...
                              </div>
                            )}
                          </div>
                          {pdfBottom5.length > 0 && (
                            <div
                              onClick={e => {
                                e.stopPropagation()
                                if (pdfSections.ranking)
                                  toggleCard('rankingBottom')
                              }}
                              style={{
                                ...cardWrap(
                                  pdfCards.rankingBottom,
                                  pdfSections.ranking
                                ),
                                background: pdfCards.rankingBottom
                                  ? '#fee2e2'
                                  : '#fff',
                                padding: 6,
                                border: pdfCards.rankingBottom
                                  ? '1px solid #dc2626'
                                  : '1.5px dashed #d1d5db'
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 7,
                                  fontWeight: 700,
                                  color: '#dc2626',
                                  textTransform: 'uppercase',
                                  marginBottom: 4
                                }}
                              >
                                Bottom {Math.min(5, pdfBottom5.length)}
                              </div>
                              {pdfBottom5.slice(0, 3).map((co, i) => (
                                <div
                                  key={co.companyKey}
                                  style={{
                                    fontSize: 8,
                                    color: '#2d2d3f',
                                    marginBottom: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <span>
                                    {pdfSortedByRoi.length -
                                      pdfBottom5.length +
                                      i +
                                      1}
                                    .{' '}
                                    {co.companyTitle.length > 18
                                      ? co.companyTitle.slice(0, 18) + '...'
                                      : co.companyTitle}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color:
                                        co.roi! >= 0 ? '#059669' : '#dc2626'
                                    }}
                                  >
                                    {formatRoiValue(co.roi!)}x
                                  </span>
                                </div>
                              ))}
                              {pdfBottom5.length > 3 && (
                                <div
                                  style={{
                                    fontSize: 7,
                                    color: '#8b8ba3',
                                    marginTop: 2
                                  }}
                                >
                                  +{pdfBottom5.length - 3} mais...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer preview */}
                  <div
                    style={{
                      borderTop: '1px solid #eeeef2',
                      paddingTop: 6,
                      marginTop: 8,
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ fontSize: 6, color: '#c4c4d4' }}>
                      Widde Video Commerce
                    </div>
                    <div style={{ fontSize: 6, color: '#c4c4d4' }}>
                      Relatorio gerado automaticamente
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'flex-end',
                    marginTop: 16
                  }}
                >
                  <button
                    onClick={() => setShowPdfModal(false)}
                    style={{
                      background: '#fff',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      padding: '10px 20px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#374151',
                      fontWeight: 500
                    }}
                  >
                    Cancelar
                  </button>
                  <PDFDownloadLink
                    document={
                      <SegmentReportPdf
                        result={result}
                        startDate={startDate}
                        endDate={endDate}
                        selectedPlanTypes={pdfSelectedPlans}
                        sections={pdfSections}
                        cards={pdfCards}
                        selectedInsights={pdfSelectedInsights}
                        filteredData={{
                          ...pdfData,
                          ranking: {
                            top5: pdfTop5,
                            bottom5: pdfBottom5,
                            sortedByRoi: pdfSortedByRoi
                          }
                        }}
                      />
                    }
                    fileName={`relatorio-${segment.toLowerCase().replace(/\s+/g, '-')}-${startDate}.pdf`}
                    style={{
                      background: '#7c3aed',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      textDecoration: 'none',
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(124,58,237,0.3)'
                    }}
                  >
                    {({ loading }) => (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {loading ? 'Gerando PDF...' : 'Gerar e Baixar'}
                      </>
                    )}
                  </PDFDownloadLink>
                </div>
              </div>
            </div>
          )
        })()}
    </div>
  )
}
